import React from 'react';
import { useTranslation } from 'react-i18next';
import { Printer, LayoutGrid, FileText, Loader2 } from 'lucide-react';

import { useGetConsignmentByIdQuery, useGetConsignmentDispositionsQuery } from '@/entities/consignments';
import type { ConsignmentDisposition, RawDisposition, RawLocation, RawMovement } from '@/entities/consignments';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';

import { useConsignmentReports } from '@/entities/consignments';
import { useProjectSiteParams } from '@/shared/lib';

import { DispositionTable } from './disposition-table';

interface ShowDispositionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  consignmentId: string | null;
}

export const ShowDispositionDialog: React.FC<ShowDispositionDialogProps> = ({ open, onOpenChange, consignmentId }) => {
  const { t } = useTranslation();
  const { projectId, siteId } = useProjectSiteParams();

  const {
    data: consignment,
    isLoading,
    isFetching
  } = useGetConsignmentByIdQuery(
    {
      id: consignmentId || '',
      projectId: projectId ? Number(projectId) : undefined,
      siteId: siteId ? Number(siteId) : undefined
    },
    { skip: !consignmentId || !open }
  );

  const { data: dispositions, isLoading: isLoadingDispositions } = useGetConsignmentDispositionsQuery(
    {
      id: consignmentId || '',
      projectId: projectId ? Number(projectId) : undefined,
      siteId: siteId ? Number(siteId) : undefined
    },
    { skip: !consignmentId || !open }
  );

  const isLoadingConsignment = isLoading || isFetching;

  const { download, isDownloadingReport, isDownloadingLabels, isDownloadingTag } = useConsignmentReports();

  const handleDownloadReport = (lang: string) => {
    if (consignmentId) download('report', { id: consignmentId, lang });
  };

  const handleDownloadLabels = (o?: number, opt: number = 1) => {
    if (consignmentId) download('labels', { id: consignmentId, o, opt });
  };

  const handleDownloadTag = (ext?: number) => {
    if (consignmentId) download('tag', { id: consignmentId, ext });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[98vw] sm:max-w-[98vw] max-h-[95vh] overflow-y-auto p-0">
        <DialogHeader className="p-6 pb-4 border-b">
          <DialogTitle className="text-2xl font-bold flex items-center gap-3">
            <span className="bg-wfp-orange w-2 h-8 rounded-full" />
            {t('dispositions.lineDisposition')}
            {consignment && (
              <span className="text-muted-foreground font-normal text-lg ml-2">#{consignment.cmrNumber}</span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="min-w-0 space-y-8 p-6 py-4">
          {/* Read-only Header Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-6 px-1">
            <Field>
              <FieldLabel>{t('consignment.fields.consignmentNumber.label')}</FieldLabel>
              {isLoadingConsignment ? (
                <Skeleton className="h-10 w-full" />
              ) : (
                <Input value={consignment?.cmrNumber || '-'} disabled />
              )}
            </Field>

            <Field>
              <FieldLabel>{t('consignment.fields.ownersReference.label')}</FieldLabel>
              {isLoadingConsignment ? (
                <Skeleton className="h-10 w-full" />
              ) : (
                <Input value={consignment?.ownersRef || '-'} disabled />
              )}
            </Field>

            <Field>
              <FieldLabel>{t('consignment.fields.trackingCode.label')}</FieldLabel>
              {isLoadingConsignment ? (
                <Skeleton className="h-10 w-full" />
              ) : (
                <Input value={consignment?.trackIdnf || '-'} disabled />
              )}
            </Field>

            <Field>
              <FieldLabel>{t('consignment.fields.priority.label')}</FieldLabel>
              {isLoadingConsignment ? (
                <Skeleton className="h-10 w-full" />
              ) : (
                <Input value={consignment?.dispatchPriorityName || '-'} disabled />
              )}
            </Field>

            <div className="lg:col-span-2">
              <Field>
                <FieldLabel>{t('consignment.fields.sender.label')}</FieldLabel>
                {isLoadingConsignment ? (
                  <Skeleton className="h-10 w-full" />
                ) : (
                  <Input
                    value={
                      consignment?.senderContact?.organization.name
                        ? `${consignment.senderContact.organization.name}${consignment.senderContact.contact.name ? ` / ${consignment.senderContact.contact.name}` : ''}`
                        : '-'
                    }
                    disabled
                  />
                )}
              </Field>
            </div>

            <div className="lg:col-span-2">
              <Field>
                <FieldLabel>{t('consignment.fields.receiver.label')}</FieldLabel>
                {isLoadingConsignment ? (
                  <Skeleton className="h-10 w-full" />
                ) : (
                  <Input
                    value={
                      consignment?.receiverContact?.organization.name
                        ? `${consignment.receiverContact.organization.name}${consignment.receiverContact.contact.name ? ` / ${consignment.receiverContact.contact.name}` : ''}`
                        : '-'
                    }
                    disabled
                  />
                )}
              </Field>
            </div>

            <div className="lg:col-span-2">
              <Field>
                <FieldLabel>{t('consignment.fields.arrivingTo.label')}</FieldLabel>
                {isLoadingConsignment ? (
                  <Skeleton className="h-10 w-full" />
                ) : (
                  <Input value={consignment?.arrivalLocationDisplayName || '-'} disabled />
                )}
              </Field>
            </div>

            <div className="lg:col-span-2">
              <Field>
                <FieldLabel>{t('consignment.fields.destination.label')}</FieldLabel>
                {isLoadingConsignment ? (
                  <Skeleton className="h-10 w-full" />
                ) : (
                  <Input value={consignment?.destinationLocationDisplayName || '-'} disabled />
                )}
              </Field>
            </div>

            <Field>
              <FieldLabel>{t('consignment.fields.status.label')}</FieldLabel>
              {isLoadingConsignment ? (
                <Skeleton className="h-10 w-full" />
              ) : (
                <Input value={consignment?.statusName || consignment?.statusCode || '-'} disabled />
              )}
            </Field>

            <div className="lg:col-span-3">
              <Field>
                <FieldLabel>{t('consignment.fields.remarks.label')}</FieldLabel>
                {isLoadingConsignment ? (
                  <Skeleton className="h-10 w-full" />
                ) : (
                  <Input value={consignment?.remarks || '-'} disabled />
                )}
              </Field>
            </div>
          </div>

          <Separator />

          {/* Dispositions Table */}
          <div className="space-y-3">
            <h3 className="text-lg font-semibold px-1">{t('dispositions.suppliesList')}</h3>
            <DispositionTable
              dispositions={
                dispositions && dispositions.length > 0
                  ? dispositions
                  : (consignment?.lines || []).map(
                      (line: RawDisposition): ConsignmentDisposition => ({
                        itemCode: line.itemCode || line.productCode || '-',
                        description: line.description || line.productDescription || '-',
                        unit: line.unit || line.uom || '-',
                        totalPerSrf: typeof line.totalPerSrf === 'number' ? line.totalPerSrf : 0,
                        remainsWithSender: line.remainsWithSender || 0,
                        cancelledBySender: line.cancelledBySender || 0,
                        receivedFromSender: line.receivedFromSender || 0,
                        transferOut: line.transferOut || 0,
                        transferIn: line.transferIn || 0,
                        stockOnHand: line.stockOnHand || 0,
                        reportedLost: line.reportedLost || 0,
                        releasedToReceiver: line.releasedToReceiver || 0,
                        locations: (line.locations || []).map((loc: RawLocation) => ({
                          locationName: loc.locationName || '-',
                          received: loc.received || 0,
                          transferOut: loc.transferOut || 0,
                          transferIn: loc.transferIn || 0,
                          stockOnHand: loc.stockOnHand || 0,
                          reportedLost: loc.reportedLost || 0,
                          released: loc.released || 0,
                          movements: (loc.movements || []).map((mov: RawMovement) => ({
                            date: mov.date || '-',
                            description: mov.description || null,
                            quantity: mov.quantity || 0,
                            stockOnHand: mov.stockOnHand || 0
                          }))
                        }))
                      })
                    )
              }
              isLoading={isLoadingDispositions || (isLoadingConsignment && !consignment)}
              isDownloadingLabels={isDownloadingLabels}
              onDownloadLabels={handleDownloadLabels}
            />
          </div>
        </div>

        <DialogFooter className="p-3 border-t bg-muted/10">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 w-full">
            <Button variant="outline" className="w-full" onClick={() => onOpenChange(false)}>
              {t('common.closeDialog', 'Close Dialog')}
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-full" disabled={isDownloadingReport}>
                  {isDownloadingReport ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Printer className="w-4 h-4 mr-2" />
                  )}
                  {t('dispositions.consignmentReport', 'Consignment Report')}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-[200px]">
                <DropdownMenuItem onClick={() => handleDownloadReport('en')}>
                  <FileText className="mr-2 w-4 h-4" /> Consignment Report (en)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleDownloadReport('es')}>
                  <FileText className="mr-2 w-4 h-4" /> Reporte de Envíos (es)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleDownloadReport('fr')}>
                  <FileText className="mr-2 w-4 h-4" /> Historique de la DSL (fr)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full text-green-700 border-green-600 hover:bg-green-50 hover:text-green-800"
                  disabled={isDownloadingTag}>
                  {isDownloadingTag ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <LayoutGrid className="w-4 h-4 mr-2" />
                  )}
                  {t('dispositions.printShippingLabel', 'Print Shipping label')}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-[250px]">
                <DropdownMenuItem onClick={() => handleDownloadTag()}>
                  <LayoutGrid className="mr-2 w-4 h-4" /> Print Shipping label
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleDownloadTag(1)}>
                  <LayoutGrid className="mr-2 w-4 h-4" /> Print Shipping label - Extended version
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
